'use client';
import { type Article, type CarouselItem as CarouselItemType } from '@/lib/types';
import { ArticleCard } from './article-card';
import { SubcategoryTabs } from './subcategory-tabs';
import { CategorySchema, BreadcrumbSchema } from '@/components/seo/JsonLdSchema';
import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
interface CategoryPageProps {
    categoryId: string;
    subcategory?: string; 
    articles: Article[]; 
    carouselItems: CarouselItemType[];
}
const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  'politics': 'Stay informed with breaking political news in Africa.',
  'economy': 'Latest economic news in Africa, featuring inflation trends, market growth, GDP analysis, and financial developments.',
  'media': 'Explore the latest developments in Africa’s media industry.',
  'business': 'Discover the latest business news in Africa, covering innovative startups and corporate strategy.',
  'technology': 'Get the latest technology news in Africa, including the digital revolution and startup ecosystems.',
  'health': 'Comprehensive health news in Africa, covering public health initiatives and research.',
  'lifestyle': 'Comprehensive health and lifestyle news in Africa, covering public health initiatives, medical research, disease management, and wellness updates.',
  'sports': 'Your source for African sports news.',
  'crime': 'In-depth journalistic reporting on crime, justice system updates, and social safety issues.',
  'world': 'Global news coverage provided through an African lens.',
  'security-conflict': 'Critical reporting on security challenges and regional stability.',
  'education': 'Latest updates on African education systems and policy changes.',
  'environment': 'Dedicated reports on African climate change and environmental conservation.'
};
const getMobileLayout = (index: number) => {
  const r = index % 7;
  if (r === 0) return 'overlay';
  if (r === 3) return 'default';
  return 'side-mobile';
};
export function CategoryPage({ categoryId, subcategory: subcategorySlug, articles: initialArticles, carouselItems }: CategoryPageProps) {
    const isMobile = useIsMobile();
    const fullCoverageArticles = useMemo(() => {
        return initialArticles;
    }, [initialArticles]);
    const filteredArticles = useMemo(() => {
        if (!subcategorySlug) return fullCoverageArticles;
        return fullCoverageArticles.filter(a => {
            const sub = (a.subcategory || '').toLowerCase().trim();
            const cleanSub = sub.replace(/\s+/g, '-').replace(/&/g, 'and');
            const simpleSub = sub.replace(/\s+/g, '-');
            return cleanSub === subcategorySlug || simpleSub === subcategorySlug;
        });
    }, [fullCoverageArticles, subcategorySlug]);
    const majorSubcategories = useMemo(() => {
        const counts: Record<string, { name: string; count: number }> = {};
        fullCoverageArticles.forEach(article => {
            if (article.subcategory) {
                const name = article.subcategory.trim();
                const key = name.toLowerCase();
                if (!counts[key]) counts[key] = { name: name, count: 0 };
                counts[key].count += 1;
            }
        });
        return Object.values(counts).filter(item => item.count >= 9).sort((a, b) => b.count - a.count);
    }, [fullCoverageArticles]);
    const categoryLower = categoryId.toLowerCase().replace(/ & /g, '-').replace(/\s+/g, '-');
    const displayTitle = categoryId;
    const description = CATEGORY_DESCRIPTIONS[categoryLower] || `In-depth reports and analysis from the ${categoryId} newsroom.`;
    const categoryUrl = `https://newsafrika.com/${categoryId.toLowerCase().replace(/\s+/g, '-')}${subcategorySlug ? `/${subcategorySlug}` : ''}`;
    const breadcrumbItems = [{ name: "Home", url: "https://newsafrika.com" }, { name: displayTitle, url: categoryUrl }];
    // Featured Logic (1 Big on left, 3 small + 1 horizontal on right)
    const featuredCount = 5;
    const mainGridArticles = useMemo(() => {
      if (isMobile) return filteredArticles;
      return filteredArticles.slice(featuredCount);
    }, [filteredArticles, isMobile, featuredCount]);
    return (
        <div className="max-w-[1440px] mx-auto px-0 xl:px-6 py-8 w-full">
            <CategorySchema name={displayTitle} url={categoryUrl} articles={filteredArticles?.slice(0, 15) || []} />
            
            <main className="w-full min-w-0">
                <div className="px-6 md:px-0"><BreadcrumbSchema items={breadcrumbItems} /></div>
                
                <div className="px-4 md:px-0 mt-8"><SubcategoryTabs categoryId={categoryId} subcategories={majorSubcategories} /></div>
                <section className="mb-12">
                    {/* FEATURED 1+4 GRID (Desktop Only) */}
                    {!isMobile && !subcategorySlug && filteredArticles.length >= featuredCount && (
                        <div className="mb-12">
                            <div className="grid grid-cols-12 md:gap-2 gap-0 items-stretch">
                                {/* Left: Lead Story */}
                                <div className="col-span-12 xl:col-span-7">
                                    <ArticleCard 
                                        article={filteredArticles[0]} 
                                        layout="overlay" 
                                        className="h-full rounded-[2rem]" 
                                        priority={true} 
                                        hideCategory={true} 
                                    />
                                </div>
                                {/* Right: Stack */}
                                <div className="col-span-12 xl:col-span-5 flex flex-col gap-2">
                                    {/* Top: 3 Small vertical items */}
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-stretch">
                                        {filteredArticles.slice(1, 4).map((article) => (
                                            <div key={article.id} className="h-full">
                                                <ArticleCard 
                                                    article={article} 
                                                    hideCategory={true} 
                                                    hideLede={true}
                                                    className="h-full"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    {/* Bottom: 1 Wide horizontal item */}
                                    <div className="pt-2 border-t border-border/50">
                                        <ArticleCard 
                                            article={filteredArticles[4]} 
                                            layout="horizontal-list" 
                                            hideCategory={true} 
                                            className="h-full md:p-0" 
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="flex items-center gap-3 mb-6 py-2 px-4 md:px-0">
                        <h2 className="text-2xl md:text-lg font-bold font-headline text-[hsl(var(--section-header))] capitalize">Latest Reports</h2>
                    </div>
                    <div className={cn(
                        "grid gap-2 md:gap-x-2 md:gap-y-3 items-stretch w-full",
                        "grid-cols-1 md:grid-cols-4"
                    )}>
                        {mainGridArticles.length > 0 ? (
                            mainGridArticles.map((article, index) => (
                                <div key={article.id} className="h-full">
                                    <ArticleCard 
                                        article={article} 
                                        mobileLayout={getMobileLayout(index)}
                                        hideCategory={true}
                                        className="h-full md:bg-transparent md:shadow-none md:border-none md:p-0"
                                    />
                                </div>
                            ))
                        ) : (
                            <div className="col-span-full py-24 text-center bg-accent/30 rounded-3xl border border-dashed border-primary/20 mx-4 md:mx-0">
                                <p className="text-xl text-white font-headline mb-4 uppercase tracking-wider">No reports filed in {displayTitle} yet.</p>
                            </div>
                        )}
                    </div>
                </section>
            </main>
        </div>
    );
}
